import { memo, useCallback } from 'react'
// @ts-expect-error TS7016 -- MIGRATION: not approved, fix when encountered
import { v4 as uuidv4 } from 'uuid'
import { NoteItem } from '../note-item/NoteItem'
import './NotesSection.scss'
import { parseUserDateTime } from '../../../../utils/common'

type NoteAuthor = {
  Prefix?: string
  FirstName?: string
  LastName?: string
  Suffix?: string
}

export type Note = {
  /** Identifies the record for the admin single-record delete (QMD-4870). */
  Id?: string | null
  Author?: NoteAuthor
  IsAddendum?: boolean
  DateTime?: string
  Message?: string
}

type Props = {
  title: string
  emptyText: string
  notesList?: Note[]
  description?: string
  isWithoutSeparator?: boolean
  className?: string
  userTimeZone?: string | undefined
  /**
   * When set, every record that has an `Id` (addenda included) gets its own delete
   * button. `summary` is the record's author and date as displayed, for the confirmation dialog.
   */
  deleteItem?:
    | {
        ariaLabel: string
        onDelete: (medicalRecordId: string, summary: string) => void
      }
    | undefined
}

export const NotesSection = memo(
  ({
    title,
    description,
    notesList,
    isWithoutSeparator,
    emptyText,
    className = '',
    userTimeZone,
    deleteItem,
  }: Props) => {
    const setProviderName = useCallback(
      (authorData: NoteAuthor | undefined, isAddendum: boolean | undefined) =>
        `${isAddendum ? 'Addendum - ' : ''}${authorData?.Prefix ? `${authorData?.Prefix} ` : ''}${
          authorData?.FirstName
        } ${authorData?.LastName}${authorData?.Suffix ? ` ${authorData?.Suffix}` : ''}`,
      [],
    )

    return (
      <div className={className}>
        <div className='notes-section-header'>
          <div
            className={`notes-section-text notes-section-title ${title === 'Note' ? 'notes-section-notes-title' : ''}`}>
            {title}
          </div>
        </div>
        {description ?
          <div className='notes-section-text notes-section-description'>{description}</div>
        : null}
        {notesList?.length ?
          notesList.map(({ Id: recordId, Author, IsAddendum, DateTime, Message }, idx) => {
            const providerName = setProviderName(Author, IsAddendum)
            const dateTime = parseUserDateTime(DateTime, userTimeZone, true)
            const summary = `${providerName}, ${dateTime}`
            return (
              <NoteItem
                // A stable key keeps the record (and its focused trash button) mounted across
                // re-renders, so focus can return to it when the delete dialog closes.
                key={recordId ?? `index-${idx}`}
                id={uuidv4()}
                providerName={providerName}
                dateTime={dateTime}
                noteValue={Message}
                isTheLast={notesList.length - 1 === idx}
                onDelete={deleteItem && recordId ? () => deleteItem.onDelete(recordId, summary) : undefined}
                deleteLabel={deleteItem ? `${deleteItem.ariaLabel}: ${summary}` : undefined}
              />
            )
          })
        : <div className='prescriptions-section-text prescriptions-section-no-prescriptions-tile'>{emptyText}</div>}
        {!isWithoutSeparator && <div className='notes-section-separator' />}
      </div>
    )
  },
)
